<template>
  <div class="min-h-screen bg-gradient-to-br from-psychology-calm via-white to-psychology-warm">
    <!-- 顶部导航 -->
    <nav class="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
      <div class="container mx-auto px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <router-link 
              to="/" 
              class="inline-flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
                <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              </svg>
            </router-link>
            <div class="flex items-center gap-2">
              <span class="text-2xl">🎯</span>
              <span class="font-semibold text-psychology-primary">霍兰德职业兴趣测试</span>
            </div>
          </div>
          <button 
            v-if="canGoPrevious"
            @click="previousQuestion" 
            class="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-md transition-colors text-gray-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
              <path d="m12 19-7-7 7-7"></path>
              <path d="M19 12H5"></path>
            </svg>
            上一题
          </button>
        </div>

        <!-- 进度条 -->
        <div class="mt-4">
          <div class="flex justify-between items-center mb-2">
            <span class="text-sm text-gray-600">
              维度 {{ currentDimensionIndex + 1 }}/6 · {{ currentDimensionName }}
            </span>
            <span class="text-sm font-medium text-psychology-primary">
              题目 {{ currentQuestionInDimension + 1 }}/15 · 总进度 {{ totalAnswered }}/90
            </span>
          </div>
          <div class="relative w-full overflow-hidden rounded-full bg-gray-200 h-2">
            <div 
              class="h-full bg-gradient-to-r from-psychology-primary to-psychology-secondary transition-all"
              :style="{ width: progressPercent + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </nav>

    <!-- 主要内容 -->
    <main class="container mx-auto px-4 py-8">
      <div class="max-w-3xl mx-auto">
        <!-- 题目卡片 -->
        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <!-- 题目头部 -->
          <div class="bg-gradient-to-r from-psychology-primary to-psychology-secondary px-4 sm:px-6 py-3 sm:py-4">
            <div class="flex items-center justify-between text-white">
              <span class="text-base sm:text-lg font-semibold">
                {{ currentDimensionName }} - 第 {{ currentQuestionInDimension }} 题
              </span>
              <span class="text-xs sm:text-sm opacity-90">还剩 {{ remainingQuestions }} 题</span>
            </div>
          </div>

          <!-- 题目内容 -->
          <div class="p-4 sm:p-6 md:p-8">
            <!-- 题目文字 -->
            <h2 class="text-lg sm:text-xl md:text-2xl font-medium text-gray-800 mb-4 sm:mb-6 md:mb-8 leading-relaxed">
              {{ currentQuestionText }}
            </h2>

            <!-- 二元选择按钮 -->
            <BinaryChoiceButton 
              :key="`question-${currentAnsweredCount}`"
              v-model="selectedAnswer" 
            />
          </div>

          <!-- 底部操作栏 -->
          <div class="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-t border-gray-100">
            <div class="flex items-center justify-between">
              <button 
                v-if="canGoPrevious"
                @click="previousQuestion"
                class="inline-flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors border border-gray-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="m12 19-7-7 7-7"></path>
                  <path d="M19 12H5"></path>
                </svg>
                上一题
              </button>
              <div v-else></div>

              <button 
                v-if="!isLastQuestion"
                @click="nextQuestion"
                :disabled="!selectedAnswer"
                :class="[
                  'inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all',
                  selectedAnswer !== null
                    ? 'bg-gradient-to-r from-psychology-primary to-psychology-secondary text-white hover:shadow-lg'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                ]"
              >
                下一题
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </button>
              <button 
                v-else
                @click="submitTest"
                :disabled="selectedAnswer === null"
                :class="[
                  'inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all',
                  selectedAnswer !== null
                    ? 'bg-gradient-to-r from-psychology-primary to-psychology-secondary text-white hover:shadow-lg'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                ]"
              >
                查看结果
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- 提示信息 -->
        <div class="mt-6 text-center text-sm text-gray-500">
          <p>💡 请根据您的真实感受评分，答题进度会自动保存</p>
        </div>
      </div>
    </main>

    <!-- 回到顶部 -->
    <BackToTop />

    <!-- 测试工具(仅开发环境) -->
    <TestQuickFill
      :answers="answers"
      :current-dimension="currentDimension"
      :current-question-in-dimension="currentQuestion"
      @update-answers="handleQuickFillAnswers"
      @submit="handleQuickSubmit"
    />
  </div>
</template>

<script>
import BackToTop from '../components/BackToTop.vue'
import BinaryChoiceButton from '../components/assessment/BinaryChoiceButton.vue'
import TestQuickFill from '../components/TestQuickFill.vue'
import { HOLLAND_QUESTIONS, DIMENSIONS } from '@/data/hollandQuestions'
import { 
  getSessionById, 
  updateSession, 
  saveSessionProgress,
  completeSession
} from '../utils/sessionStorage.js'
import { calculateHollandResult } from '@/utils/hollandCalculator'
import { useToast } from 'vue-toastification'

export default {
  name: 'QuestionnairePage',
  components: {
    BackToTop,
    BinaryChoiceButton,
    TestQuickFill
  },
  data() {
    return {
      sessionId: '',
      session: null,
      currentAnsweredCount: 0,
      answers: [],
      selectedAnswer: null,
      isSubmitting: false,
      toast: null
    }
  },
  computed: {
    currentQuestion() {
      return HOLLAND_QUESTIONS[this.currentAnsweredCount] || HOLLAND_QUESTIONS[0]
    },
    currentQuestionText() {
      return this.currentQuestion?.text || ''
    },
    currentDimensionCode() {
      return this.currentQuestion?.dimension || 'R'
    },
    currentDimensionName() {
      return DIMENSIONS[this.currentDimensionCode]?.name || ''
    },
    currentDimensionIndex() {
      const order = ['R', 'I', 'A', 'S', 'E', 'C']
      return order.indexOf(this.currentDimensionCode) + 1
    },
    currentQuestionInDimension() {
      const dimension = this.currentDimensionCode
      const questionsInDimension = HOLLAND_QUESTIONS.filter(q => q.dimension === dimension)
      const currentIndex = questionsInDimension.findIndex(q => q.id === this.currentQuestion.id)
      return currentIndex + 1
    },
    totalAnswered() {
      return this.answers.length
    },
    progressPercent() {
      return Math.round((this.totalAnswered / 90) * 100)
    },
    remainingQuestions() {
      return 90 - this.totalAnswered
    },
    canGoPrevious() {
      return this.totalAnswered > 0
    },
    isLastQuestion() {
      return this.currentAnsweredCount === 89
    }
  },
  watch: {
    selectedAnswer(newVal) {
      if (newVal !== null) {
        this.handleAnswerSelected()
      }
    }
  },
  created() {
    // 初始化 toast
    this.toast = useToast()
    this.initializeSession()
  },
  beforeUnmount() {
    // 清除自动跳转定时器
    if (this.autoJumpTimer) {
      clearTimeout(this.autoJumpTimer)
      this.autoJumpTimer = null
    }
  },
  methods: {
    initializeSession() {
      // 获取sessionId
      this.sessionId = this.$route.query.sessionId
      
      if (!this.sessionId) {
        this.toast.error('会话ID缺失，请重新开始测试')
        this.$router.push('/assessment/info')
        return
      }

      // 加载会话数据
      this.session = getSessionById(this.sessionId)
      
      if (!this.session) {
        this.toast.error('会话不存在，请重新开始测试')
        this.$router.push('/assessment/info')
        return
      }

      // 恢复会话状态
      this.answers = this.session.answers || []
      this.currentAnsweredCount = this.answers.length

      // 如果当前题目已经有答案，加载它
      const existingAnswer = this.answers[this.currentAnsweredCount]
      if (existingAnswer) {
        this.selectedAnswer = existingAnswer.answer
      }

      console.log('Session initialized:', {
        sessionId: this.sessionId,
        currentAnsweredCount: this.currentAnsweredCount,
        totalAnswers: this.answers.length
      })
    },
    
    /**
     * 处理答案选择
     */
    handleAnswerSelected() {
      if (this.selectedAnswer === null) return
      
      // 保存答案
      this.saveAnswer()
      
      // 自动跳转到下一题
      setTimeout(() => {
        if (this.isLastQuestion) {
          // 最后一题不自动跳转
          return
        }
        this.nextQuestion()
      }, 300)
    },
    
    saveAnswer() {
      if (this.selectedAnswer === null) return

      const answer = {
        id: this.currentQuestion.id,
        dimension: this.currentQuestion.dimension,
        answer: this.selectedAnswer,
        text: this.currentQuestion.text
      }

      // 添加答案
      if (this.currentAnsweredCount < this.answers.length) {
        this.answers[this.currentAnsweredCount] = answer
      } else {
        this.answers.push(answer)
      }

      // 保存到会话
      this.saveProgress()
    },

    saveProgress() {
      // 更新会话数据
      updateSession(this.sessionId, {
        currentAnsweredCount: this.currentAnsweredCount,
        answers: this.answers
      })

      // 保存进度
      saveSessionProgress(this.sessionId, {
        currentAnsweredCount: this.currentAnsweredCount,
        answers: this.answers
      })
    },

    nextQuestion() {
      if (this.selectedAnswer === null) {
        this.toast.warning('请先选择答案')
        return
      }

      // 保存当前答案
      this.saveAnswer()

      // 移动到下一题
      if (this.currentAnsweredCount < 89) {
        this.currentAnsweredCount++
        this.selectedAnswer = null

        // 检查新位置是否已有答案
        const existingAnswer = this.answers[this.currentAnsweredCount]
        if (existingAnswer) {
          this.selectedAnswer = existingAnswer.answer
        }
      }

      // 保存进度
      this.saveProgress()

      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    previousQuestion() {
      if (!this.canGoPrevious) return

      // 清除可能存在的定时器
      if (this.autoJumpTimer) {
        clearTimeout(this.autoJumpTimer)
        this.autoJumpTimer = null
      }

      // 保存当前答案(如果有)
      if (this.selectedAnswer !== null) {
        this.saveAnswer()
      }

      // 移动到上一题
      if (this.currentAnsweredCount > 0) {
        this.currentAnsweredCount--
      }

      // 加载上一题的答案
      const existingAnswer = this.answers[this.currentAnsweredCount]
      this.selectedAnswer = existingAnswer ? existingAnswer.answer : null

      // 保存进度
      this.saveProgress()

      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    async submitTest() {
      if (this.selectedAnswer === null) {
        this.toast.warning('请先选择答案')
        return
      }

      if (this.isSubmitting) return
      this.isSubmitting = true

      try {
        // 保存最后一题的答案
        this.saveAnswer()

        // 验证答案完整性
        if (this.answers.length !== 90) {
          this.toast.error(`答题不完整，当前已答 ${this.answers.length}/90 题`)
          this.isSubmitting = false
          return
        }

        // 计算结果
        const result = calculateHollandResult(
          this.answers,
          this.session.userInfo || {}
        )

        console.log('Holland test completed, result:', result)

        // 完成会话
        completeSession(this.sessionId, result)

        // 跳转到计算页面
        this.$router.push({
          path: '/assessment/calculating',
          query: {
            sessionId: this.sessionId
          }
        })
      } catch (error) {
        console.error('Submit test error:', error)
        this.toast.error('提交失败，请重试')
        this.isSubmitting = false
      }
    },

    /**
     * 处理快速填充答案(开发环境)
     */
    handleQuickFillAnswers(newAnswers) {
      // newAnswers是一个长度为90的数组,每个元素是0或1
      this.answers = newAnswers.map((answer, index) => ({
        id: HOLLAND_QUESTIONS[index].id,
        dimension: HOLLAND_QUESTIONS[index].dimension,
        answer: answer,
        text: HOLLAND_QUESTIONS[index].text
      }))

      // 更新当前选中的答案
      this.selectedAnswer = newAnswers[this.currentAnsweredCount]

      // 保存进度
      this.saveProgress()
    },

    /**
     * 处理快速提交(开发环境)
     */
    async handleQuickSubmit() {
      if (this.answers.length !== 90) {
        this.toast.error(`答题不完整，当前已答 ${this.answers.length}/90 题`)
        return
      }

      if (this.isSubmitting) return
      this.isSubmitting = true

      try {
        // 计算结果
        const result = calculateHollandResult(
          this.answers,
          this.session.userInfo || {}
        )

        console.log('Quick submit completed, result:', result)

        // 完成会话
        completeSession(this.sessionId, result)

        // 显示成功提示
        //this.toast.success('测试完成！正在生成结果...')

        // 跳转到计算页面，传递 sessionId
        this.$router.push({
          path: '/assessment/calculating',
          query: {
            sessionId: this.sessionId
          }
        })
      } catch (error) {
        console.error('Quick submit error:', error)
        this.toast.error('提交失败，请重试')
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

button:not(:disabled):hover {
  transform: translateY(-2px);
}

button:not(:disabled):active {
  transform: translateY(0);
}
</style>
